import React, { useMemo, useState, useRef } from "react";
import { useLocation } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@apollo/client/react";
import sortBy from "lodash/sortBy";

import {
  PROJECTS_NAMES_QUERY,
  SIMPLE_INVENTORY_REQUESTS_QUERY,
  SHOPPING_INVENTORY_REQUESTS_COLLECTION_QUERY,
} from "../../constants/graphql/queries";
import { fullWidthDialog } from "../../constants/dialog";
import useAuth from "../../hooks/useAuth";

const rowsPerPage = 20;

const titles = {
  orders: "Select Order",
  reservations: "Select Reservation",
};

const columnHeaders = {
  orders: "Order #",
  reservations: "Reservation #",
};

const buttonLabels = {
  orders: "Select Order",
  reservations: "Select Reservation",
};

export default function InventoryRequestPickerModal({
  type = "orders",
  open = true,
  onClose,
  onSelect,
  actionLabel,
  emptyActionLabel,
  onEmptyAction,
  queryVariables = {},
}) {
  const location = useLocation();
  const { user } = useAuth();
  const isShopping = location.pathname.includes("shopping");
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const dialogContentRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState(String(queryVariables.projectId || ""));

  const query = isShopping
    ? SHOPPING_INVENTORY_REQUESTS_COLLECTION_QUERY
    : SIMPLE_INVENTORY_REQUESTS_QUERY;
  const responseKey = isShopping ? "shoppingInventoryRequestsCollection" : "inventoryRequests";
  const filterByProject = type === "reservations" && !isShopping;
  const projectIdValue = selectedProjectId ? Number(selectedProjectId) : undefined;
  const variables = {
    ...queryVariables,
    ...(filterByProject ? { projectId: projectIdValue } : {}),
    type,
    limit: rowsPerPage,
    page: page + 1,
    search: searchTerm,
  };

  const { loading, data, refetch } = useQuery(query, {
    variables,
  });
  const { data: { projects = [] } = {} } = useQuery(PROJECTS_NAMES_QUERY, {
    skip: !filterByProject,
  });

  const orders = data?.[responseKey]?.collection || [];
  const totalCount = data?.[responseKey]?.metadata?.totalCount || 0;
  const projectOptions = useMemo(
    () =>
      [
        { label: `All ${user.company?.settings?.filter_field3 || "Projects"}`, value: "" },
        ...sortBy(projects, "name").map(project => ({
          label: project.name,
          value: String(project.id),
        })),
      ],
    [projects, user.company?.settings?.filter_field3]
  );

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchValue);
    refetch({
      ...variables,
      page: 1,
      search: searchValue,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage - 1);
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  };

  const handleProjectChange = event => {
    const nextProjectId = event.target.value;
    setPage(0);
    setSelectedProjectId(nextProjectId);
    refetch({
      ...variables,
      page: 1,
      projectId: nextProjectId ? Number(nextProjectId) : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      onClose={onClose}
      sx={{
        ...fullWidthDialog,
        "& .MuiDialog-paper": {
          ...fullWidthDialog["& .MuiDialog-paper"],
          maxHeight: { xs: "78vh", sm: "50vh" },
          width: { xs: "100%", sm: "900px" },
          maxWidth: { xs: "100%", sm: "900px" },
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: { xs: "78vh", sm: "50vh" },
          width: { xs: "100%", sm: "900px" },
          maxWidth: { xs: "100%", sm: "900px" },
          margin: { xs: 0, sm: 4 },
        },
      }}
    >
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={22} fontWeight={500} color="textPrimary" align="center">
          {titles[type]}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon color="action" />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={dialogContentRef}>
        <Box mt={1} mb={3} sx={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 2 }}>
          <Box display="flex" flexDirection="column" gap={isMobile ? 1.5 : 0}>
            {filterByProject && isMobile && (
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedProjectId}
                  onChange={handleProjectChange}
                  displayEmpty
                  renderValue={value => {
                    const selectedProject = projectOptions.find(option => option.value === value);
                    return selectedProject?.label || `Choose a ${user.company?.settings?.filter_field2 || "Project"}`;
                  }}
                >
                  {projectOptions.map(option => (
                    <MenuItem key={option.value || "all"} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              size="small"
              label="Search"
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: filterByProject && !isMobile ? (
                  <InputAdornment position="start" sx={{ maxWidth: 260, mr: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value={selectedProjectId}
                        onChange={handleProjectChange}
                        displayEmpty
                        variant="standard"
                        disableUnderline
                        renderValue={value => {
                          const selectedProject = projectOptions.find(option => option.value === value);
                          return selectedProject?.label || `Choose a ${user.company?.settings?.filter_field2 || "Project"}`;
                        }}
                        sx={{
                          fontSize: 14,
                          minHeight: 24,
                          ".MuiSelect-select": {
                            py: 0,
                          },
                        }}
                      >
                        {projectOptions.map(option => (
                          <MenuItem key={option.value || "all"} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  </InputAdornment>
                ) : undefined,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <DialogContentText>
          {loading && <LinearProgress />}
          {!loading && orders.length === 0 && (
            <Typography variant="body1" align="center" sx={{ my: 4 }}>
              No {type} found matching your search.
            </Typography>
          )}

          {orders.length > 0 && isMobile && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {orders.map(order => (
                <Card key={order.id} sx={{ boxShadow: 1 }}>
                  <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                    <Box mb={2}>
                      <Typography color="text.secondary" fontWeight={500}>
                        {columnHeaders[type].split(" ")[0]} # {order.workOrderNumber}
                      </Typography>
                      <Typography mt={1} color="primary" sx={{ wordBreak: "break-word" }}>
                        {order.user.email}
                      </Typography>
                      {order.comment && (
                        <Typography mt={1} variant="body2" color="text.secondary">
                          {order.comment}
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={() => onSelect(order)}
                    >
                      {actionLabel || buttonLabels[type]}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {orders.length > 0 && !isMobile && (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {columnHeaders[type]}
                      </TableCell>
                      <TableCell align="center">Requested By</TableCell>
                      <TableCell align="center">Notes</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow
                        key={order.id}
                        selected={selectedOrder?.id === order.id}
                        hover
                        onClick={() => handleSelectOrder(order)}
                      >
                        <TableCell align="center">
                          {order.workOrderNumber}
                        </TableCell>
                        <TableCell align="center">{order.user.email}</TableCell>
                        <TableCell align="center">
                          {order.comment || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => onSelect(order)}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {actionLabel || buttonLabels[type]}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", p: 2 }}>
        {!loading && orders.length === 0 && onEmptyAction && (
          <Button variant="contained" onClick={onEmptyAction}>
            {emptyActionLabel}
          </Button>
        )}
        {totalCount > rowsPerPage && (
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
